import { IconArrowLeft } from "@/assets/icons/geist/IconArrowLeft";
import { type FC, useMemo, useState, useEffect } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { KeyboardKey } from "@/components/KeyboardKey";
import { Link } from "@/components/flexkit/Link";
import { useEscapeShortcut } from "@/hooks/use-escape-shortcut";
import { Button } from "@/components/flexkit/Button";
import { ArchivedEnvironmentsList } from "@/components/archive/ArchivedEnvironmentsList";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { ListEnvironmentsRequest_ArchivalStatus } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useEnvironmentsByProjects } from "@/hooks/use-environments-by-projects";
import { environmentsByProjectsToArchivedEnvironments, type ArchivedEnvironment } from "@/utils/environment";
import { ArchivedEnvironmentDeleteModal } from "@/components/archive/ArchivedEnvironmentDeleteModal";

type ArchivedSidebarProps = {
    onArchiveViewChange: (isArchive: boolean) => void;
};

export const ArchivedSidebarContent: FC<ArchivedSidebarProps> = ({ onArchiveViewChange }) => {
    const { data: environmentsByProjects, isLoading: isLoading } = useEnvironmentsByProjects({
        archivalStatus: ListEnvironmentsRequest_ArchivalStatus.ARCHIVED,
    });

    const archivedEnvironments = useMemo(
        () => (environmentsByProjects ? environmentsByProjectsToArchivedEnvironments(environmentsByProjects) : []),
        [environmentsByProjects],
    );

    const [environmentsToDelete, setEnvironmentsToDelete] = useState<ArchivedEnvironment[]>([]);

    const handleDeleteAll = () => {
        setEnvironmentsToDelete(archivedEnvironments);
    };

    const handleDeleteSingle = (environment: ArchivedEnvironment) => {
        setEnvironmentsToDelete([environment]);
    };

    useEscapeShortcut(() => {
        onArchiveViewChange(false);
    });

    return (
        <>
            <div className="px-2">
                <SidebarHeader onArchiveViewChange={onArchiveViewChange} />
            </div>
            <div className="mr-0.5 flex-grow pt-2">
                <ArchivedEnvironmentsList
                    archivedEnvironments={archivedEnvironments}
                    isLoading={isLoading}
                    onDelete={handleDeleteSingle}
                />
            </div>
            <div className="pb-2">
                <SidebarFooter
                    archivedEnvironments={archivedEnvironments}
                    isLoading={isLoading}
                    onDeleteAll={handleDeleteAll}
                />
            </div>
            {environmentsToDelete.length > 0 && (
                <ArchivedEnvironmentDeleteModal
                    onClose={() => setEnvironmentsToDelete([])}
                    archivedEnvironments={environmentsToDelete}
                />
            )}
        </>
    );
};

type SidebarHeaderProps = {
    onArchiveViewChange: (isArchive: boolean) => void;
};

const SidebarHeader: FC<SidebarHeaderProps> = ({ onArchiveViewChange }) => {
    return (
        <Tooltip
            content={
                <div className="flex flex-row items-center gap-2">
                    Archived environments
                    <KeyboardKey className="bg-surface-invert-tertiary">Esc</KeyboardKey>
                </div>
            }
        >
            <Link
                to="#"
                className="flex h-8 flex-row items-center gap-1 rounded-lg pl-2 hover:bg-surface-03"
                onClick={() => onArchiveViewChange(false)}
                reloadIfFrontendUpdated={true}
            >
                <div className="w-5">
                    <IconArrowLeft size="base" />
                </div>

                <div className={cn("flex-grow truncate text-start text-base font-bold")}>Archived environments</div>
            </Link>
        </Tooltip>
    );
};

type SidebarFooterProps = {
    archivedEnvironments: ArchivedEnvironment[];
    isLoading?: boolean;
    onDeleteAll: () => void;
};

const SidebarFooter: FC<SidebarFooterProps> = ({ archivedEnvironments, isLoading, onDeleteAll }) => {
    const [isButtonEnabled, setIsButtonEnabled] = useState(false);

    useEffect(() => {
        if (archivedEnvironments.length > 0 && !isButtonEnabled) {
            const timer = setTimeout(() => {
                setIsButtonEnabled(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [archivedEnvironments.length, isButtonEnabled]);

    return (
        <div>
            {isLoading ? (
                <div className="mt-4 flex flex-col gap-[4px] pl-2 pr-4">
                    <SkeletonBlock className="h-8 w-full" ready={false} />
                </div>
            ) : (
                archivedEnvironments.length > 0 && (
                    <div className="mt-4 flex flex-col gap-[4px] pl-2 pr-4">
                        <Button
                            variant="destructive"
                            className="w-full"
                            onClick={onDeleteAll}
                            disabled={!isButtonEnabled}
                        >
                            Delete All
                        </Button>
                    </div>
                )
            )}
        </div>
    );
};
